/**
 * Wochendiagramm der Liquiditaetsplanung: je Woche zwei gestapelte Saeulen - Gesichert (unten
 * Kontostand, oben Sicherungen; Hoehe = gesichert) und Faelligkeit (unten Fix faellig, oben Geplant;
 * Hoehe = fix + geplant) -, wahlweise
 * "Kumuliert" (Standard) oder "Je Woche". Verglichen wird die Gesamtfaelligkeit gegen die Sicherung. Optik und Masse stammen vom bisherigen `WochenDiagramm`
 * (`.fx-week-chart-*` in `bestand.css`: Achsenstufung, Balkenhoehe 176 px, Mindesthoehe 4 px,
 * Wertbeschriftung `fxWeekChartValue`); Ergaenzungen in `styles/neubau/liquiditaet.css`.
 *
 * Jede Wochengruppe ist ein Knopf: Klick waehlt die Woche und filtert die Ratentabelle, ein
 * zweiter Klick hebt auf. Die gewaehlte Ansicht merkt sich der Browser (localStorage, mit
 * try/catch - der Speicher kann gesperrt sein). Fehlt sie oder ist sie ungueltig, gilt "kumuliert";
 * nur ein gespeichertes "woche" stellt "Je Woche" wieder her.
 */
import { fmtDate, fmtUsd } from "../../lib/format.ts";
import { fxWeekChartValue } from "../../lib/fx.ts";
import { wochenLabel, type LiquiditaetsWoche } from "../../lib/liquiditaet.ts";

export type DiagrammModus = "woche" | "kumuliert";

export const MODUS_SCHLUESSEL = "hubLiquiditaetDiagrammModus";

export function leseModus(): DiagrammModus {
  try {
    return window.localStorage.getItem(MODUS_SCHLUESSEL) === "woche" ? "woche" : "kumuliert";
  } catch { // still: ein blockiertes localStorage (privates Fenster o. ae.) darf die Seite nicht verhindern - es gilt dann die Vorgabe "kumuliert"
    return "kumuliert";
  }
}

export function schreibeModus(modus: DiagrammModus): void {
  try {
    window.localStorage.setItem(MODUS_SCHLUESSEL, modus);
  } catch { // still: Speicher gesperrt oder voll - die Auswahl gilt dann nur fuer diese Sitzung, kein Datenverlust
  }
}

export interface LiquiditaetsDiagrammProps {
  wochen: readonly LiquiditaetsWoche[];
  modus: DiagrammModus;
  onModus: (modus: DiagrammModus) => void;
  /** Schluessel der gewaehlten Woche, `null` = keine. */
  auswahl: string | null;
  onWoche: (key: string) => void;
}

const MINUS = "−";

/** Mantissen der Achsenstufen (x 10^n): Das Maximum nutzt so immer mindestens 75 % der Achsenhoehe. */
const ACHSEN_MANTISSEN = [1, 1.2, 1.6, 2, 2.4, 3, 4, 5, 6, 8, 10] as const;

/** Obergrenze der Achse: kleinste Stufe >= Maximum (mindestens 1.000); die Mitte ist stets eine runde Zahl. */
export function achsenOben(maximum: number): number {
  const wert = Math.max(1000, Number.isFinite(maximum) ? maximum : 0);
  const zehner = 10 ** Math.floor(Math.log10(wert));
  const mantisse = wert / zehner;
  const stufe = ACHSEN_MANTISSEN.find((m) => m >= mantisse - 1e-9) ?? 10;
  return Math.round(stufe * zehner);
}

/** Kurzform fuer die Beschriftung im Segment (hoechstens 6 Zeichen, damit sie in die Balkenbreite passt). */
export function segmentKurz(wert: number): string {
  const zahl = (n: number): string => String(n).replace(".", ",");
  if (wert >= 1_000_000) return `${zahl(Math.round(wert / 100_000) / 10)} M`;
  if (wert >= 100_000) return `${Math.round(wert / 1000)} T`;
  if (wert >= 1000) return `${zahl(Math.round(wert / 100) / 10)} T`;
  return String(Math.round(wert));
}

function werte(woche: LiquiditaetsWoche, modus: DiagrammModus) {
  return modus === "kumuliert"
    ? {
        gesichert: woche.gesichertKum, kontostand: woche.kontostandKum, sicherung: woche.sicherungKum,
        fix: woche.fixKum, geplant: woche.geplantKum, delta: woche.saldo,
      }
    : {
        gesichert: woche.gesichert, kontostand: woche.kontostand, sicherung: woche.sicherung,
        fix: woche.fix, geplant: woche.geplant, delta: woche.gesichert - woche.fix - woche.geplant,
      };
}

/** Teilwert im Segment nur bei zwei Segmenten (bei einem steht die Summe darueber) und wo die Hoehe ihn traegt. */
const SEGMENT_TEXT_AB_PX = 18;

interface StapelSegment {
  /** CSS-Klasse des Segments (`covered`, `sicherung`, `due`, `geplant`). */
  klasse: string;
  wert: number;
}

/**
 * Eine gestapelte Saeule mit Summe darueber: Segmente unten -> oben, Hoehe = Summe gegen die Achse,
 * Segmente anteilig. Segmente <= 0 werden nicht gezeichnet; die Anteile beziehen sich auf die gezeichneten,
 * damit ein negativer Kontostand kein Segment ueber 100 % treibt (die Saeule ist dann nur kuerzer).
 */
function Saeule({ segmente, hoehe }: { segmente: readonly StapelSegment[]; hoehe: (wert: number) => number }) {
  const summe = segmente.reduce((a, s) => a + s.wert, 0);
  const stapelHoehe = hoehe(summe);
  const gezeichnet = segmente.filter((s) => s.wert > 0);
  const zweiSegmente = gezeichnet.length === 2;
  const anteilBasis = gezeichnet.reduce((a, s) => a + s.wert, 0);
  return (
    <div className="fx-week-chart-bar-col">
      <span className="fx-week-chart-value">{summe ? fxWeekChartValue(summe) : "–"}</span>
      <div className="liq-stapel" style={{ height: `${stapelHoehe}px` }}>
        {/* DOM oben -> unten: das letzte Segment der Liste steht zuerst */}
        {[...gezeichnet].reverse().map((s) => (
          <div className={`fx-week-chart-bar ${s.klasse}`} key={s.klasse} style={{ height: `${(s.wert / anteilBasis) * 100}%` }}>
            {zweiSegmente && stapelHoehe * (s.wert / anteilBasis) >= SEGMENT_TEXT_AB_PX ? (
              <span className="liq-segment-wert">{segmentKurz(s.wert)}</span>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}

export function LiquiditaetsDiagramm({ wochen, modus, onModus, auswahl, onWoche }: LiquiditaetsDiagrammProps) {
  const alle = wochen.map((w) => werte(w, modus));
  // Beide Saeulen sind Stapel (Kontostand + Sicherungen, Fix + Geplant); die Achse muss die hoehere abdecken.
  const maxValue = Math.max(1, ...alle.flatMap((w) => [w.gesichert, w.fix + w.geplant]));
  const axisTop = achsenOben(maxValue);
  const barHeight = (value: number): number => (value > 0 ? Math.max(4, Math.round((value / axisTop) * 176)) : 0);

  return (
    <div className="fx-week-chart-card liq-diagramm">
      <div className="fx-week-chart-head">
        <div>
          <strong>Liquidität je Kalenderwoche</strong>
          <span>
            Gesichert (gestapelt) = Kontostand (aktuelle Woche) unten plus offene Kurssicherungen nach Valutadatum oben.
            Fix und geplant = USD-Raten nach Fälligkeit. Raten ohne Datum stehen in keiner Woche.
          </span>
        </div>
        <div className="liq-diagramm-rechts">
          <div className="liq-segment" role="group" aria-label="Darstellung">
            <button type="button" aria-pressed={modus === "kumuliert"} onClick={() => onModus("kumuliert")}>
              Kumuliert
            </button>
            <button type="button" aria-pressed={modus === "woche"} onClick={() => onModus("woche")}>
              Je Woche
            </button>
          </div>
          <div className="fx-week-chart-legend">
            <span><i className="covered"></i>Kontostand</span>
            <span><i className="sicherung"></i>Sicherungen</span>
            <span><i className="due"></i>Fix fällig</span>
            <span><i className="geplant"></i>Geplant (gestapelt)</span>
          </div>
        </div>
      </div>
      <div className="fx-week-chart-frame">
        <div className="fx-week-chart-axis">
          <span>{fxWeekChartValue(axisTop)}</span>
          <span>{fxWeekChartValue(axisTop / 2)}</span>
          <span>$ 0</span>
        </div>
        <div className="fx-week-chart-scroll" tabIndex={0} aria-label="Balkendiagramm Liquidität nach Kalenderwoche">
          <div className="fx-week-chart">
            {wochen.map((woche, i) => {
              const w = alle[i];
              const label = woche.spaeter ? "Später" : woche.ueberfaellig ? "Überfällig" : `KW ${String(woche.kw).padStart(2, "0")}`;
              const jahr = woche.start ? String(woche.jahr) : "";
              const range = woche.start
                ? `${fmtDate(woche.start)} – ${fmtDate(woche.ende)}`
                : woche.spaeter ? "nach dem Planungshorizont" : "vor heute";
              const deltaText = `${w.delta >= 0 ? "+" : MINUS} ${fxWeekChartValue(Math.abs(w.delta))}`;
              const gewaehlt = auswahl === woche.key;
              const beschreibung =
                `${wochenLabel(woche)}: gesichert ${fmtUsd(w.gesichert)} (Kontostand ${fmtUsd(w.kontostand)}, ` +
                `Sicherungen ${fmtUsd(w.sicherung)}), fix fällig ${fmtUsd(w.fix)}, ` +
                `geplant ${fmtUsd(w.geplant)}, ${modus === "kumuliert" ? "Saldo" : "Differenz"} ` +
                `${w.delta < 0 ? MINUS : ""}${fmtUsd(Math.abs(w.delta))}`;
              const klassen = [
                "fx-week-chart-group",
                "liq-woche",
                woche.aktuell ? "current" : "",
                woche.ueberfaellig ? "ueberfaellig" : "",
                woche.spaeter ? "spaeter" : "",
                gewaehlt ? "gewaehlt" : "",
                modus === "kumuliert" && woche.reichweite ? "reichweite" : "",
              ].filter(Boolean).join(" ");
              return (
                <button
                  type="button"
                  className={klassen}
                  key={woche.key}
                  aria-pressed={gewaehlt}
                  aria-label={beschreibung}
                  title={beschreibung}
                  onClick={() => onWoche(woche.key)}
                >
                  <div className="fx-week-chart-bars">
                    <Saeule
                      segmente={[{ klasse: "covered", wert: w.kontostand }, { klasse: "sicherung", wert: w.sicherung }]}
                      hoehe={barHeight}
                    />
                    <Saeule
                      segmente={[{ klasse: "due", wert: w.fix }, { klasse: "geplant", wert: w.geplant }]}
                      hoehe={barHeight}
                    />
                  </div>
                  <div className="fx-week-chart-label">
                    <strong>{label}</strong>
                    <span>{`${jahr ? `${jahr} · ` : ""}${range}`}</span>
                  </div>
                  <div className={`fx-week-chart-delta ${w.delta >= 0 ? "positive" : "negative"}`}>
                    {deltaText}
                    {modus === "kumuliert" && woche.reichweite ? <b className="liq-reichweite-marke"> · Unterdeckung</b> : null}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
